<script setup>
import { computed, useSlots } from 'vue';
import CFIcon from '@/ui/Icon/CFIcon.vue';
import { alertColors, theme } from './index.js';

const props = defineProps({
    /**
     * @defaultValue 'info'
     * @values 'info' | 'success' | 'warning' | 'error'
     */
    color: {
        type: String,
        default: 'info',
        validator: (v) => alertColors.includes(v),
    },
    title: {
        type: String,
        default: undefined,
    },
    icon: {
        /** @type import('vue').PropType<IconName> */
        type: String,
        default: null,
    },
    /**
     * @defaultValue 'vertical'
     */
    orientation: {
        type: String,
        default: 'vertical',
        validator: (v) => ['vertical', 'horizontal'].includes(v),
    },
    /**
     * Shows a dismiss button if true
     * @defaultValue false
     */
    dismissible: {
        type: Boolean,
        default: false,
    },
    ui: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(['close']);

const slots = useSlots();

const ui = computed(() =>
    theme({
        color: props.color,
        orientation: props.orientation,
    })
);
</script>

<template>
    <div
        :class="ui.base({ class: [props.ui?.base, props.class] })"
        :data-orientation="orientation"
    >
        <CFIcon
            v-if="icon"
            :name="icon"
            variant="bold"
            :class="ui.icon({ class: props.ui?.icon })"
            size="16"
        />
        <div
            class="flex min-w-0 flex-1 gap-2"
            :class="{
                'flex-col': orientation === 'vertical',
                'flex-row items-center': orientation === 'horizontal',
            }"
        >
            <div
                v-if="title || !!slots.title"
                :class="ui.title({ class: props.ui?.title })"
            >
                <slot name="title">
                    {{ title }}
                </slot>
            </div>
            <div
                v-if="!!slots.default"
                :class="ui.content({ class: props.ui?.content })"
            >
                <slot />
            </div>

            <div
                v-if="orientation === 'vertical' && !!slots.actions"
                :class="ui.actions({ class: props.ui?.actions })"
            >
                <slot name="actions" />
            </div>
        </div>
        <div
            v-if="dismissible"
            class="flex shrink-0 flex-wrap items-center gap-1.5"
        >
            <template v-if="orientation === 'horizontal' && !!slots.actions">
                <slot name="actions" />
            </template>

            <button
                type="button"
                :class="ui.dismissButton({ class: props.ui?.dismissButton })"
                :aria-label="$t('button.close')"
                @click="emit('close')"
            >
                <span class="text-sm font-normal underline underline-offset-4">{{ $t('button.close') }}</span>
                <CFIcon
                    name="remove-circle"
                    size="16"
                    aria-hidden="true"
                />
            </button>
        </div>
    </div>
</template>
